import React from 'react';
import { Button, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useDispatch, useSelector } from 'react-redux';
import { isNaN, isNumber } from 'lodash';

import { ICartItem, IGuestCartItem } from '../interfaces';
import { useCartItemsStyles } from '../hooks/useCartItemsStyles.';
import { selectIsAuthorized } from '../selectors';
import { actions as cartActions } from '../../../../store/ducks/cart.duck';
import { getMaxQuantity } from '../../../../utils/utils';

export function CartCounter({ item }: { item: ICartItem | IGuestCartItem }) {
  const isAuthorized = useSelector(selectIsAuthorized);
  const dispatch = useDispatch();
  const classes = useCartItemsStyles();
  const [editable, setEditable] = React.useState(false);
  const [value, setValue] = React.useState(1);

  const maxQuantityLevel =
    'priceValue' in item
      ? getMaxQuantity(
          item.priceValue?.vendor?.quantity_level,
          item.priceValue?.vendor?.second_quantity_level
        )
      : getMaxQuantity(
          item.price_offer?.quantity_level,
          item.price_offer?.second_quantity_level
        );
  const quantity = maxQuantityLevel ? 1000000000 : 0;
  const maxQuantity =
    'priceValue' in item
      ? (item.priceValue.vendor?.order_quantity || 0) +
        (item.priceValue.vendor?.stock_quantity || 0)
      : (item.price_offer?.order_quantity || 0) + (item.price_offer?.stock_quantity || 0);
  const minCount =
    'vendor_id' in item
      ? item?.prices?.[0]?.amount || 0
      : item?.priceValue?.prices?.[0]?.amount || 0;
  const maxCount = maxQuantity || quantity;

  const handleEditable = (item: ICartItem | IGuestCartItem) => {
    if (value === 0) {
      if (isAuthorized) {
        if ('vendor_id' in item) {
          dispatch(
            cartActions.setProductCountRequest({
              product_id: item.product.id || 0,
              count: 0,
              vendor_id: item.vendor_id,
            })
          );
        }
      } else if ('priceValue' in item) {
        dispatch(
          cartActions.setCountGuestCart({
            product_id: item.product.id || 0,
            count: 0,
            type: 'dec',
            priceValue: item.priceValue,
          })
        );
      }
    } else if (isAuthorized) {
      if ('vendor_id' in item) {
        const minValue = value < minCount ? 0 : value;
        dispatch(
          cartActions.setProductCountRequest({
            product_id: item.product.id || 0,
            count: value > maxCount ? maxCount : minValue,
            vendor_id: item.vendor_id,
          })
        );
      }
    } else if ('priceValue' in item) {
      const minValue = value < minCount ? 0 : value;
      const countValue = value > maxCount ? maxCount : minValue;
      dispatch(
        cartActions.setCountGuestCart({
          product_id: item.product.id || 0,
          count: countValue,
          type: countValue > item.count ? 'inc' : 'dec',
          priceValue: item.priceValue,
        })
      );
    }
  };

  return (
    <div className={classes.containerBtns}>
      <Button
        color='primary'
        sx={{
          height: 51,
          color: '#a3a7b880',
          cursor: 'pointer',
        }}
        onClick={() => {
          const newCount = +item.count - 1;
          const minValue = newCount < minCount ? 0 : newCount;
          const countValue = newCount > maxCount ? maxCount : minValue;
          if (isAuthorized) {
            if ('vendor_id' in item) {
              dispatch(
                cartActions.setProductCountRequest({
                  product_id: item.product.id || 0,
                  count: countValue,
                  vendor_id: item.vendor_id,
                })
              );
            }
          } else if ('priceValue' in item) {
            dispatch(
              cartActions.setCountGuestCart({
                product_id: item.product.id || 0,
                count: countValue,
                type: countValue > item.count ? 'inc' : 'dec',
                priceValue: item.priceValue,
              })
            );
          }
        }}
      >
        <RemoveIcon htmlColor='#858996' fontSize='small' />
      </Button>
      {editable ? (
        <TextField
          autoFocus
          margin='normal'
          value={value}
          onChange={e => {
            if (!isNumber(+e.target.value) || +e.target.value < 0 || isNaN(+e.target.value)) {
              setValue(item.count);
              return;
            }
            if (e.target.value === '') {
              setValue(0);
            } else {
              setValue(+e.target.value);
            }
          }}
          inputProps={{ style: { textAlign: 'center', paddingLeft: 0, paddingRight: 0 } }}
          sx={{
            margin: 0,
            padding: 0,
            height: 51,
            minWidth: 60,
            border: 'none',
          }}
          onBlur={() => {
            if (value !== item.count) {
              if (isNumber(+value) && +value >= 0) {
                handleEditable(item);
              }
            }
            setEditable(false);
          }}
          onKeyUp={event => {
            if (event.key === 'Enter') {
              if (value !== item.count) {
                if (isNumber(+value) && +value >= 0) {
                  handleEditable(item);
                }
              }
              setEditable(false);
            }
          }}
          variant='outlined'
          autoComplete='off'
        />
      ) : (
        <div
          onClick={() => {
            setEditable(true);
            setValue(item.count);
          }}
          className={classes.buttonCount}
        >
          {item.count}
        </div>
      )}
      <Button
        color='primary'
        sx={{
          height: 51,
          color: '#a3a7b880',
          cursor: 'pointer',
        }}
        onClick={() => {
          const newCount = +item.count + 1;
          const minValue = newCount < minCount ? 0 : newCount;
          const countValue = newCount > maxCount ? maxCount : minValue;
          if (isAuthorized) {
            if ('vendor_id' in item) {
              dispatch(
                cartActions.setProductCountRequest({
                  product_id: item.product.id || 0,
                  count: countValue,
                  vendor_id: item.vendor_id,
                })
              );
            }
          } else if ('priceValue' in item) {
            dispatch(
              cartActions.setCountGuestCart({
                product_id: item.product.id || 0,
                count: countValue,
                type: countValue > item.count ? 'inc' : 'dec',
                priceValue: item.priceValue,
              })
            );
          }
        }}
      >
        <AddIcon htmlColor='#858996' fontSize='small' />
      </Button>
    </div>
  );
}
